<template>
  <div>
    <b-alert v-if="!formData || !hasAnyData" show variant="info">
      <feather-icon icon="InfoIcon" class="mr-50" />
      The client has not started filling the form yet.
    </b-alert>

    <template v-else>
      <b-alert v-if="isSigned" show variant="success" class="p-1">
        <feather-icon icon="CheckCircleIcon" class="mr-50" />
        Signed by <strong>{{ onboarding.SignatureName || "—" }}</strong> on
        <strong>{{ onboarding.SignedDate }} {{ onboarding.SignedTime }}</strong>
        <template v-if="onboarding.SignedIp"> from IP <strong>{{ onboarding.SignedIp }}</strong></template>
      </b-alert>
      <b-alert v-if="onboarding.Status === 'Reopened'" show variant="warning" class="p-1">
        <feather-icon icon="RefreshCwIcon" class="mr-50" />
        The form was re-opened: new sections or questions were added after it was signed. The client
        has to sign again to cover
        <strong>{{ pendingSectionTitles.join(", ") || "the new sections" }}</strong>.
      </b-alert>

      <b-card
        v-for="section in sections"
        :key="section.key"
        :title="sectionTitle(section)"
        class="border mb-1"
      >
        <small
          v-if="(section.fromPackages && section.fromPackages.length) || isSigned"
          class="text-muted d-block mb-1"
          style="margin-top:-0.75rem"
        >
          <template v-if="section.fromPackages && section.fromPackages.length">
            From package{{ section.fromPackages.length > 1 ? "s" : "" }}: {{ section.fromPackages.join(", ") }}
          </template>
          <b-badge v-if="isSectionSigned(section.key)" variant="light-success" class="ml-25">Signed</b-badge>
          <b-badge v-else-if="isSigned" variant="light-warning" class="ml-25">Not yet signed</b-badge>
        </small>

        <!-- ================= Person sections: one nested card per entry ================= -->
        <template v-if="section.personType">
          <p v-if="!personEntries(section).length" class="text-muted mb-0">
            No {{ personPlural(section.personType).toLowerCase() }} entered.
          </p>
          <b-card
            v-for="(entry, index) in personEntries(section)"
            :key="section.key + (entry.personId || 'i' + index)"
            :title="personLabel(section.personType) + ' ' + (index + 1) + (entry.name ? ' — ' + entry.name : '')"
            class="border mb-1"
          >
            <b-row>
              <template v-for="item in visibleItems(section, entry)">
                <!-- heading -->
                <b-col v-if="item.inputType === 'heading'" :key="item.key" cols="12">
                  <h6 class="mt-50 mb-50">{{ item.label }}</h6>
                </b-col>

                <!-- person signature -->
                <b-col v-else-if="item.builtinKey === 'personSignature'" :key="item.key" cols="12" md="6" class="mb-50">
                  <small class="text-muted d-block">{{ item.label || "Signature" }}</small>
                  <img
                    v-if="personSignature(entry, index)"
                    :src="personSignature(entry, index)"
                    alt="Signature"
                    class="border rounded bg-white"
                    style="max-height:120px; max-width:100%"
                  />
                  <span v-else class="text-muted">Not signed</span>
                </b-col>

                <!-- notice inside a person section -->
                <b-col v-else-if="item.kind === 'Notice'" :key="item.key" cols="12" class="mb-50">
                  <p class="mb-0">
                    <feather-icon
                      :icon="isAcknowledged(entry[item.dataKey]) ? 'CheckSquareIcon' : 'SquareIcon'"
                      :class="isAcknowledged(entry[item.dataKey]) ? 'text-success' : 'text-muted'"
                      class="mr-50"
                    />
                    {{ noticeTitle(item) }} —
                    {{ isAcknowledged(entry[item.dataKey]) ? "acknowledged" : "not acknowledged" }}
                  </p>
                </b-col>

                <!-- plain question (personName included) -->
                <b-col v-else :key="item.key" cols="12" :md="colWidth(item)" class="mb-50">
                  <small class="text-muted d-block">{{ item.label }}</small>
                  <span :style="item.inputType === 'textarea' ? 'white-space:pre-wrap' : ''">
                    {{ displayAnswer(item, item.builtinKey === "personName" ? entry.name : entry[item.dataKey]) }}
                  </span>
                </b-col>
              </template>

              <!-- legacy fallback: no schema, dump every key of the entry -->
              <template v-if="!section.items">
                <b-col v-for="row in legacyRows(entry)" :key="row.key" cols="12" md="6" class="mb-50">
                  <small class="text-muted d-block">{{ row.label }}</small>
                  <span>{{ row.value || "—" }}</span>
                </b-col>
                <b-col v-if="personSignature(entry, index)" cols="12" md="6" class="mb-50">
                  <small class="text-muted d-block">Signature</small>
                  <img
                    :src="personSignature(entry, index)"
                    alt="Signature"
                    class="border rounded bg-white"
                    style="max-height:120px; max-width:100%"
                  />
                </b-col>
              </template>
            </b-row>
          </b-card>
        </template>

        <!-- ================= Account sections driven by the schema ================= -->
        <b-row v-else-if="section.items">
          <template v-for="item in visibleItems(section)">
            <!-- heading -->
            <b-col v-if="item.inputType === 'heading'" :key="item.key" cols="12">
              <h6 class="mt-50 mb-50">{{ item.label }}</h6>
            </b-col>

            <!-- declaration notice (system) -->
            <template v-else-if="item.kind === 'Notice' && item.key === 'DECLARATION'">
              <b-col :key="item.key + ':name'" cols="12" md="6" class="mb-50">
                <small class="text-muted d-block">Name</small>
                <span>{{ declarationName(item) }}</span>
                <small class="text-muted d-block mt-50">Date</small>
                <span>{{ onboarding.SignedDate || "—" }}</span>
              </b-col>
              <b-col :key="item.key + ':sig'" cols="12" md="6" class="mb-50">
                <small class="text-muted d-block">Signature</small>
                <img
                  v-if="signatureImages.declaration"
                  :src="signatureImages.declaration"
                  alt="Signature"
                  class="border rounded bg-white"
                  style="max-height:120px; max-width:100%"
                />
                <span v-else class="text-muted">Not signed</span>
              </b-col>
            </template>

            <!-- notice acknowledged by signature -->
            <template v-else-if="item.kind === 'Notice' && item.notice && item.notice.ackMode === 'Signature'">
              <b-col :key="item.key + ':name'" cols="12" md="6" class="mb-50">
                <small class="text-muted d-block">{{ noticeTitle(item) }}</small>
                <span class="d-block">
                  <feather-icon
                    :icon="noticeSignature(item) ? 'CheckSquareIcon' : 'SquareIcon'"
                    :class="noticeSignature(item) ? 'text-success' : 'text-muted'"
                    class="mr-50"
                  />
                  {{ noticeSignature(item) ? "signed" : "not signed" }}
                </span>
                <small class="text-muted d-block mt-50">Name</small>
                <span>{{ noticeName(item) }}</span>
                <template v-if="noticeSignedAt(item)">
                  <small class="text-muted d-block mt-50">Date</small>
                  <span>{{ noticeSignedAt(item) }}</span>
                </template>
              </b-col>
              <b-col :key="item.key + ':sig'" cols="12" md="6" class="mb-50">
                <small class="text-muted d-block">Signature</small>
                <img
                  v-if="noticeSignature(item)"
                  :src="noticeSignature(item)"
                  alt="Signature"
                  class="border rounded bg-white"
                  style="max-height:120px; max-width:100%"
                />
                <span v-else class="text-muted">Not signed</span>
              </b-col>
            </template>

            <!-- notice acknowledged by tick -->
            <b-col v-else-if="item.kind === 'Notice'" :key="item.key" cols="12" class="mb-50">
              <p class="mb-0">
                <feather-icon
                  :icon="isAcknowledged(answer(item)) ? 'CheckSquareIcon' : 'SquareIcon'"
                  :class="isAcknowledged(answer(item)) ? 'text-success' : 'text-muted'"
                  class="mr-50"
                />
                {{ noticeTitle(item) }} —
                {{ isAcknowledged(answer(item)) ? "acknowledged" : "not acknowledged" }}
              </p>
            </b-col>

            <!-- re-categorisation builtin -->
            <template v-else-if="item.builtinKey === 'recategorisation'">
              <b-col :key="item.key + ':class'" cols="12" md="6" class="mb-50">
                <small class="text-muted d-block">{{ item.label || "Requested classification" }}</small>
                <span>{{ classificationLabel(answer(item)) }}</span>
                <template v-if="recatCriteria(answer(item)).length">
                  <small class="text-muted d-block mt-50">Reclassification criteria (Retail → Professional)</small>
                  <span v-for="c in recatCriteria(answer(item))" :key="c" class="d-block">• {{ c }}</span>
                </template>
                <small class="text-muted d-block mt-50">Name</small>
                <span>{{ recatName(answer(item)) }}</span>
              </b-col>
              <b-col :key="item.key + ':sig'" cols="12" md="6" class="mb-50">
                <small class="text-muted d-block">Signature</small>
                <img
                  v-if="signatureImages.recategorisation"
                  :src="signatureImages.recategorisation"
                  alt="Signature"
                  class="border rounded bg-white"
                  style="max-height:120px; max-width:100%"
                />
                <span v-else class="text-muted">Not signed</span>
              </b-col>
            </template>

            <!-- plain question (clientType builtin included) -->
            <b-col v-else :key="item.key" cols="12" :md="colWidth(item)" class="mb-50">
              <small class="text-muted d-block">{{ item.label }}</small>
              <span :style="item.inputType === 'textarea' ? 'white-space:pre-wrap' : ''">
                {{ displayAnswer(item, answer(item)) }}
              </span>
            </b-col>
          </template>
          <b-col v-if="!visibleItems(section).length" cols="12">
            <span class="text-muted">—</span>
          </b-col>
        </b-row>

        <!-- ================= Legacy fallback: no schema, dump the section object ================= -->
        <b-row v-else-if="section.dataKey && legacyRows(formData[section.dataKey]).length">
          <b-col v-for="row in legacyRows(formData[section.dataKey])" :key="row.key" cols="12" md="6" class="mb-50">
            <small class="text-muted d-block">{{ row.label }}</small>
            <span style="white-space:pre-wrap">{{ row.value || "—" }}</span>
          </b-col>
          <b-col v-if="section.key === 'F' && signatureImages.recategorisation" cols="12" md="6" class="mb-50">
            <small class="text-muted d-block">Signature</small>
            <img :src="signatureImages.recategorisation" alt="Signature" class="border rounded bg-white" style="max-height:120px; max-width:100%" />
          </b-col>
          <b-col v-if="section.key === 'E' && signatureImages.declaration" cols="12" md="6" class="mb-50">
            <small class="text-muted d-block">Signature</small>
            <img :src="signatureImages.declaration" alt="Signature" class="border rounded bg-white" style="max-height:120px; max-width:100%" />
          </b-col>
        </b-row>
        <span v-else class="text-muted">—</span>
      </b-card>
    </template>
  </div>
</template>

<script>
import { getPath, isHidden } from "@/views/onboarding/public/formSchema";
import { PERSON_TYPES } from "@/views/onboarding/public/personTypes";

// Fallbacks used only when the payload carries no form schema (old data).
const DEFAULT_DATA_KEYS = {
  A: "sectionA",
  B: "sectionB",
  C: "sectionC",
  D: "sectionD",
  E: "sectionE",
  F: "sectionF",
  H: "sectionH",
};

const ENTRY_HIDDEN_KEYS = ["personId", "signatureImage", "signatureFile", "legacyAuthorisedTrader"];

const CLASSIFICATION_LABELS = {
  Professional: "Professional Client (per se or elective)",
  Retail: "Retail Client (re-classification request)",
  EligibleCounterparty: "Eligible Counterparty (re-classification request)",
};

const CRITERIA_LABELS = {
  transactions: "10+ significant transactions per quarter over the last four quarters",
  portfolio: "Financial portfolio exceeding EUR 500,000",
  professional: "Worked in the financial sector for at least one year in a professional position",
};

function isEmptyValue(value) {
  if (value === null || value === undefined || value === "" || value === false) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.values(value).every(isEmptyValue);
  return false;
}

export default {
  name: "OnboardingFormTab",

  props: {
    onboarding: { type: Object, default: () => ({}) },
    formData: { type: Object, default: null },
    formSchema: { type: Object, default: () => ({ sections: [] }) },
    formSections: { type: Array, default: () => [] },
    sectionCatalogue: { type: Array, default: () => [] },
    personTypes: { type: Object, default: () => ({}) },
    summary: { type: Object, default: () => ({}) },
    signatureImages: { type: Object, default: () => ({}) },
  },

  computed: {
    isSigned() {
      return !!(this.onboarding && this.onboarding.SignedDate);
    },

    hasSchema() {
      return !!(this.formSchema && Array.isArray(this.formSchema.sections) && this.formSchema.sections.length);
    },

    signedSectionKeys() {
      let signed = this.onboarding ? this.onboarding.SignedSections : null;
      if (typeof signed === "string") {
        try {
          signed = JSON.parse(signed);
        } catch (e) {
          signed = [];
        }
      }
      return Array.isArray(signed) ? signed : [];
    },

    noticeSignatures() {
      let notices = this.onboarding ? this.onboarding.NoticeSignatures : null;
      if (typeof notices === "string") {
        try {
          notices = JSON.parse(notices);
        } catch (e) {
          notices = {};
        }
      }
      return notices && typeof notices === "object" ? notices : {};
    },

    pendingSectionTitles() {
      const pending = (this.summary && this.summary.sectionsPending) || [];
      return pending.map((key) => {
        const section = this.sections.find((s) => s.key === key);
        return section ? section.title : key;
      });
    },

    // Sections to show, in display order. With a schema each section carries its items;
    // without one (legacy data) the list comes from formSections / the catalogue and the
    // section's values are dumped generically.
    sections() {
      if (this.hasSchema) {
        return this.formSchema.sections.map((section) => {
          const applied = this.formSections.find((s) => s.key === section.key) || {};
          const catalogue = this.sectionCatalogue.find((c) => c.key === section.key) || {};
          return {
            key: section.key,
            title: section.title || applied.title || catalogue.title || section.key,
            subtitle: section.subtitle || null,
            personType: section.personType || null,
            formKey: section.formKey || null,
            items: Array.isArray(section.items) ? section.items : [],
            fromPackages: this.sectionPackages(section, applied),
          };
        });
      }

      const list = this.formSections.length
        ? this.formSections
        : this.sectionCatalogue.filter((s) => !s.personType || s.key === "G");
      return list.map((section) => {
        const catalogue = this.sectionCatalogue.find((c) => c.key === section.key) || {};
        const personType = section.personType || catalogue.personType || null;
        return {
          key: section.key,
          title: section.title || catalogue.title || section.key,
          subtitle: null,
          personType,
          formKey: personType ? this.personFormKey(personType) : null,
          items: null,
          dataKey: catalogue.dataKey || DEFAULT_DATA_KEYS[section.key] || null,
          fromPackages: section.fromPackages || [],
        };
      });
    },

    hasAnyData() {
      if (!this.formData) return false;
      const persons = this.formData.persons || {};
      if (Object.values(persons).some((list) => Array.isArray(list) && list.length)) return true;
      return Object.keys(this.formData).some((key) => key !== "persons" && !isEmptyValue(this.formData[key]));
    },
  },

  methods: {
    /* ---------- sections ---------- */
    sectionTitle(section) {
      const letter = section.key.length === 1 ? `Section ${section.key} — ` : "";
      return letter + section.title;
    },

    sectionPackages(section, applied) {
      if (applied.fromPackages && applied.fromPackages.length) return applied.fromPackages;
      const names = [];
      (section.items || []).forEach((item) => {
        (item.fromPackages || []).forEach((name) => {
          if (!names.includes(name)) names.push(name);
        });
      });
      return names;
    },

    isSectionSigned(key) {
      return this.signedSectionKeys.includes(key);
    },

    // Items to render: everything except those hidden by a condition on the current answers.
    visibleItems(section, entry) {
      if (!section.items) return [];
      return section.items.filter((item) => !isHidden(item, this.formData || {}, entry));
    },

    colWidth(item) {
      return item.cols && item.cols >= 12 ? 12 : 6;
    },

    /* ---------- persons ---------- */
    personType(type) {
      return (this.personTypes && this.personTypes[type]) || {};
    },

    personLabel(type) {
      const local = PERSON_TYPES[type] || {};
      return this.personType(type).label || local.singular || type;
    },

    personPlural(type) {
      const local = PERSON_TYPES[type] || {};
      return this.personType(type).plural || local.plural || `${this.personLabel(type)}s`;
    },

    personFormKey(type) {
      const local = PERSON_TYPES[type] || {};
      return this.personType(type).formKey || local.formKey || null;
    },

    personEntries(section) {
      const formKey = section.formKey || this.personFormKey(section.personType);
      const persons = (this.formData && this.formData.persons) || {};
      return formKey && Array.isArray(persons[formKey]) ? persons[formKey] : [];
    },

    personSignature(entry, index) {
      if (entry.personId && this.signatureImages[`person:${entry.personId}`]) {
        return this.signatureImages[`person:${entry.personId}`];
      }
      return this.signatureImages[`trader:${index}`] || null;
    },

    /* ---------- answers ---------- */
    answer(item) {
      return getPath(this.formData, item.dataKey);
    },

    optionLabel(item, value) {
      const option = (item.options || []).find((opt) =>
        typeof opt === "object" ? String(opt.value) === String(value) : String(opt) === String(value)
      );
      if (!option) return String(value);
      return typeof option === "object" ? String(option.text || option.label || option.value) : String(option);
    },

    displayAnswer(item, value) {
      if (value === null || value === undefined || value === "") return "—";
      switch (item.inputType) {
        case "checkbox":
          return value ? "Yes" : "No";
        case "checkboxes":
          return Array.isArray(value) && value.length ? value.map((v) => this.optionLabel(item, v)).join(", ") : "—";
        case "radio":
        case "select":
          return this.optionLabel(item, value);
        default:
          return this.displayValue(value);
      }
    },

    displayValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (value === true) return "Yes";
      if (value === false) return "No";
      if (Array.isArray(value)) return value.length ? value.map((v) => this.displayValue(v)).join(", ") : "—";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },

    /* ---------- notices ---------- */
    noticeTitle(item) {
      return (item.notice && item.notice.title) || item.label || item.key;
    },

    isAcknowledged(value) {
      return !!(value && typeof value === "object" && value.acknowledged);
    },

    noticeSignature(item) {
      return this.signatureImages[`notice:${item.key}`] || null;
    },

    noticeRecord(item) {
      return this.noticeSignatures[item.key] || null;
    },

    noticeName(item) {
      const record = this.noticeRecord(item);
      const ack = this.answer(item);
      return (record && record.name) || (ack && typeof ack === "object" && ack.name) || "—";
    },

    noticeSignedAt(item) {
      const record = this.noticeRecord(item);
      return record && record.signedAt ? String(record.signedAt).replace("T", " ").substring(0, 19) : "";
    },

    declarationName(item) {
      const ack = this.answer(item);
      return (
        this.onboarding.SignatureName ||
        (ack && typeof ack === "object" && ack.name) ||
        (this.formData.sectionE && this.formData.sectionE.name) ||
        "—"
      );
    },

    /* ---------- re-categorisation ---------- */
    classificationLabel(value) {
      const classification = value && typeof value === "object" ? value.classification : value;
      return CLASSIFICATION_LABELS[classification] || "—";
    },

    recatCriteria(value) {
      const criteria = (value && typeof value === "object" && value.criteria) || {};
      return Object.keys(CRITERIA_LABELS)
        .filter((key) => criteria[key])
        .map((key) => CRITERIA_LABELS[key]);
    },

    recatName(value) {
      return this.onboarding.RecatSignatureName || (value && typeof value === "object" && value.name) || "—";
    },

    /* ---------- legacy fallback ---------- */
    legacyRows(object) {
      if (!object || typeof object !== "object") return [];
      return Object.keys(object)
        .filter((key) => !ENTRY_HIDDEN_KEYS.includes(key))
        .map((key) => ({ key, label: this.humanise(key), value: this.displayValue(object[key]) }));
    },

    humanise(key) {
      return String(key)
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (c) => c.toUpperCase());
    },
  },
};
</script>
